function getZipConstructor() {
	if(typeof globalThis.JSZip !== 'function') {
		throw new Error('The TB2/TB3 project loader is unavailable until JSZip has loaded.');
	}
	return globalThis.JSZip;
}

function normalizeAudioData(audioData, fallbackName) {
	const channels = Math.max(1, Math.floor(+audioData.channels || 1));
	const data = audioData.data instanceof Float32Array ? audioData.data :
		new Float32Array(Array.isArray(audioData.data) ? audioData.data : []);
	return {
		name: audioData.name || fallbackName,
		data,
		channels,
		sampleRate: +audioData.sampleRate || 8000,
		duration: data.length / channels / (+audioData.sampleRate || 8000)
	};
}

function readZipEntry(zipData, name) {
	const entry = zipData.files[name];
	return entry && !entry.dir ? entry : null;
}

export async function loadExoticProject(file) {
	const Zip = getZipConstructor();
	const zipData = await new Zip().loadAsync(file);
	const codeEntry = readZipEntry(zipData, 'code.txt');
	const settingsEntry = readZipEntry(zipData, 'settings.json');
	if(!codeEntry || !settingsEntry) {
		throw new Error('This project is missing code.txt or settings.json.');
	}
	const code = await codeEntry.async('string');
	const settings = JSON.parse(await settingsEntry.async('string'));
	const audioFiles = new Map();
	const audioNames = Object.keys(zipData.files).filter(name => /^audio\/\d+\.json$/i.test(name));
	for(const name of audioNames) {
		const index = +name.match(/\/(\d+)\.json$/)[1];
		const audioData = JSON.parse(await zipData.files[name].async('string'));
		audioFiles.set(index, normalizeAudioData(audioData, name));
	}
	const legacyAudioEntry = readZipEntry(zipData, 'audio.json');
	if(legacyAudioEntry) {
		const audioData = JSON.parse(await legacyAudioEntry.async('string'));
		const channels = Math.max(1, Math.floor(+audioData.channels ||
			(Array.isArray(audioData.data?.[0]) ? audioData.data[0].length : 1)));
		const frames = Array.isArray(audioData.data) ? audioData.data : [];
		const interleaved = new Float32Array(frames.length * channels);
		for(let frame = 0; frame < frames.length; ++frame) {
			if(Array.isArray(frames[frame])) {
				for(let channel = 0; channel < channels; ++channel) {
					interleaved[frame * channels + channel] = +frames[frame][channel] || 0;
				}
			} else {
				interleaved[frame * channels] = +frames[frame] || 0;
			}
		}
		audioFiles.set(0, normalizeAudioData({
			name: 'audio.json',
			data: interleaved,
			channels,
			sampleRate: audioData.sampleRate
		}, 'audio.json'));
	}
	return {
		code,
		settings,
		format: audioNames.length ? 'TB3' : 'TB2',
		audioFiles
	};
}

export async function decodeAudioFile(audioContext, file) {
	const audioBuffer = await audioContext.decodeAudioData(await file.arrayBuffer());
	const channels = audioBuffer.numberOfChannels;
	const interleaved = new Float32Array(audioBuffer.length * channels);
	for(let frame = 0; frame < audioBuffer.length; ++frame) {
		for(let channel = 0; channel < channels; ++channel) {
			interleaved[frame * channels + channel] = audioBuffer.getChannelData(channel)[frame];
		}
	}
	return {
		name: file.name,
		data: interleaved,
		channels,
		sampleRate: audioBuffer.sampleRate,
		duration: audioBuffer.duration
	};
}

export async function saveTB3Project({ code, settings, audioFiles }) {
	const Zip = getZipConstructor();
	const zip = new Zip();
	zip.file('code.txt', code || '');
	zip.file('settings.json', JSON.stringify(settings));
	const audioFolder = zip.folder('audio');
	for(const [index, audioData] of audioFiles) {
		audioFolder.file(`${ index }.json`, JSON.stringify({
			name: audioData.name,
			data: Array.from(audioData.data),
			channels: audioData.channels,
			sampleRate: audioData.sampleRate
		}));
	}
	return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}
